import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Space, Typography } from 'antd';
import moment from 'moment';

import PageHeader from '../../uiKitComponents/pageHeader';
import Form from '../../uiKitComponents/form';
import DatePicker from '../../uiKitComponents/datePicker';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';
import Table from '../../uiKitComponents/table';
import PaginationSummary from '../../components/PaginationSummary';

import {
  getEmailDeliverabilityReport,
  cleanReports,
  setCleanOnUnmountTrue as setReportsCleanOnUnmountTrue,
} from '../../store/actions/reportPagesAction';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const paginationInitialState = {
  page: 1,
};

const TableComponent = ({ title, dataSource, columns, loading }) => {
  const [state, setState] = useSessionStorage(
    'mail-deliverability-domain-breakdown',
    {
      ...paginationInitialState,
    },
  );

  const onTableChange = useCallback(
    (pagination, _, __, { action }) => {
      const newState = {};
      if (action === 'paginate') {
        newState.page = pagination.current;
      }
      setState((prevState) => ({ ...prevState, ...newState }));
    },
    [setState],
  );

  const pagination = useMemo(() => {
    const per_page = 25;
    const total = dataSource?.length || 0;

    return {
      page: state.page,
      per_page,
      total,
      from: (state.page - 1) * per_page + 1,
      to: Math.min(state.page * per_page, total),
    };
  }, [dataSource, state.page]);

  return (
    <div style={{ flex: 1 }}>
      <Title level={4}>{title}</Title>
      <Table
        size="middle"
        columns={columns}
        rowKey="country"
        dataSource={dataSource}
        loading={loading}
        onChange={onTableChange}
        pagination={{
          position: ['bottomRight'],
          showSizeChanger: false,
          showQuickJumper: true,
          pageSize: pagination?.per_page,
          total: pagination?.total,
          current: pagination?.page,
        }}
      />
      <PaginationSummary meta={pagination} />
    </div>
  );
};

TableComponent.defaultProps = {
  title: '',
  dataSource: [],
  columns: [],
  loading: false,
};

TableComponent.propTypes = {
  title: PropTypes.string,
  dataSource: PropTypes.array,
  columns: PropTypes.array,
  loading: PropTypes.bool,
};

const getMomentDate = (date) => (date ? moment(date, 'YYYY-MM-DD') : null);

const domainBreakdownColumns = [
  {
    dataIndex: 'domain',
    title: 'Domain',
  },
  {
    dataIndex: 'total',
    title: 'Total',
  },
];

const templateColumns = [
  {
    dataIndex: 'event',
    title: 'Event',
  },
  {
    dataIndex: 'template_id',
    title: 'Template ID',
  },
  {
    dataIndex: 'total',
    title: 'Total',
  },
];

const trendColumns = [
  {
    dataIndex: 'event_date',
    title: 'Date',
    render: (date) => moment(date).format('DD/MM/YYYY'),
  },
  {
    dataIndex: 'delivery_status',
    title: 'Delivery Status',
  },
  {
    dataIndex: 'total',
    title: 'Total',
  },
];

const initialState = {
  dates: { from: null, to: null },
};

const MailDeliverabilityPage = () => {
  const [form] = Form.useForm();

  const [state, setState] = useSessionStorage('mail-deliverability', {
    ...initialState,
  });
  const { dates } = state;

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const { cleanOnUnmount, emailDeliverability } = useSelector((store) => ({
    cleanOnUnmount: store.reportPages.cleanOnUnmount,
    emailDeliverability: store.reportPages.payload.emailDeliverability,
  }));

  const dispatch = useDispatch();

  const fetchData = useCallback(() => {
    setLoading(true);

    getEmailDeliverabilityReport({
      from: dates?.from?.string,
      to: dates?.to?.string,
    })(dispatch)
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('mail-deliverability');
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, dates]);

  useEffect(() => {
    fetchData();
  }, [dispatch, fetchData]);

  useEffect(() => {
    if (emailDeliverability?.selected_range) {
      const { from, to } = emailDeliverability.selected_range;
      form.setFieldsValue({ range: [getMomentDate(from), getMomentDate(to)] });
    }
  }, [emailDeliverability?.selected_range, form]);

  useMount(() => {
    setReportsCleanOnUnmountTrue()(dispatch);
  });

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('mail-deliverability');
      cleanReports()(dispatch);
    }
  });

  const onSubmit = ({ range }) => {
    setState((prevState) => ({
      ...prevState,
      dates:
        range && range.length
          ? {
              from: {
                string: moment(range[0].$d).format('DD-MM-YYYY'),
                locale: range[0],
              },
              to: {
                string: moment(range[1].$d).format('DD-MM-YYYY'),
                locale: range[1],
              },
            }
          : initialState.dates,
    }));
  };

  const onReset = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      dates: initialState.dates,
    }));
  };

  if (error)
    return (
      <PageHeader
        className="page-main-header"
        title="Deliverability & Suppression"
      >
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader
        className="page-main-header"
        title="Deliverability & Suppression"
      >
        <Card className="header-card">
          <h3>Select Dates</h3>
          <Form
            form={form}
            onFinish={onSubmit}
            onReset={onReset}
            className="d-flex align-items-center justify-content-between w-100"
          >
            <Form.Item name="range">
              <RangePicker
                format="DD/MM/YYYY"
                disabledDate={(date) => date.isAfter(moment())}
              />
            </Form.Item>
            <Space>
              <Form.Item>
                <Button type="dashed" htmlType="reset">
                  Clear
                </Button>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </Form.Item>
            </Space>
          </Form>
        </Card>
      </PageHeader>
      <div className="summary-container">
        {Object.entries(emailDeliverability?.summary || {}).map(
          ([key, value]) => (
            <button
              type="button"
              key={key}
              className={'summary-el summary-el--read-only'}
            >
              <span className="text-capitalize">
                {key.split('_').join(' ')}:{' '}
              </span>
              <span>{value}</span>
            </button>
          ),
        )}
      </div>
      <div
        className="page-main-content d-flex align-items-start"
        style={{ gap: 32 }}
      >
        <TableComponent
          title="Domain Breakdown"
          dataSource={emailDeliverability?.domain_breakdown}
          columns={domainBreakdownColumns}
          loading={loading}
        />
        <TableComponent
          title="Template Breakdown"
          dataSource={emailDeliverability?.template_stats}
          columns={templateColumns}
          loading={loading}
        />
        <TableComponent
          title="Trend"
          dataSource={emailDeliverability?.trend}
          columns={trendColumns}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default MailDeliverabilityPage;
